import type { Time } from "~/tools/date-time/time";
import { filter } from "~/tools/filter";
import { invert } from "~/tools/invert";
import { isPresent } from "~/tools/is-present";
import { isShallowEqual } from "~/tools/is-shallow-equal";
import { map } from "~/tools/map";
import { isNonEmptyArray } from "~/tools/non-empty-array";
import { pipe } from "~/tools/pipe";
import { prop } from "~/tools/prop";
import { type SortRule, sortBy } from "~/tools/sort-by";

const NEXT_SORTING = {
	none: "asc",
	asc: "desc",
	desc: "none",
} as const;

const PREV_SORTING = invert(NEXT_SORTING);

export type TableColOrderValue = "asc" | "desc";

export type TableColOrderIterate<TItem> = [
	/**
	 * If the getter returns nullable value, it will be sorted to the end of the list.
	 */
	getter: (item: TItem) => null | undefined | string | number | Date,
	order: undefined | TableColOrder,
];

export interface TableColOrderInitOptions {
	/**
	 * @default "none"
	 */
	order?: TableColOrderValue;

	/**
	 * Lower priority comes first.
	 *
	 * @default undefined
	 */
	priority?: number;
}

export class TableColOrder {
	private static readonly DEFAULT = new TableColOrder(undefined, undefined, undefined);

	public static init({ order, priority }: TableColOrderInitOptions = {}): TableColOrder {
		return new TableColOrder(order, priority, undefined);
	}

	public static orderBy<TItem>(
		items: ReadonlyArray<TItem>,
		...entries: ReadonlyArray<TableColOrderIterate<TItem>>
	): ReadonlyArray<TItem> {
		const sorters = TableColOrder.sort(entries, prop(1)).map(
			([[getter], order]) =>
				[
					(item) => {
						const value = getter(item);

						if (value != null) {
							return value;
						}

						// push the nullable values to the end of the list
						return order === "asc" ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
					},
					order,
				] satisfies SortRule<TItem>,
		);

		if (!isNonEmptyArray(sorters)) {
			return items;
		}

		return sortBy(items, ...sorters);
	}

	/**
	 * Sorts orders. First it sorts by priority, then by updatedAt.
	 */
	public static sort<TEntry>(
		entries: ReadonlyArray<TEntry>,
		getOrder: (entry: TEntry) => undefined | TableColOrder,
	): Array<[TEntry, TableColOrderValue]> {
		return pipe(
			entries,
			map((entry) => {
				const order = getOrder(entry) ?? TableColOrder.DEFAULT;

				if (order.value == null) {
					return null;
				}

				return {
					entry,
					value: order.value,
					priority: order.priority ?? entries.length,
					updatedAt: order.updatedAt ?? 0,
				};
			}),
			filter(isPresent),
			sortBy(prop("priority"), prop("updatedAt")),
			map(({ entry, value }): [TEntry, TableColOrderValue] => [entry, value]),
		);
	}

	public isEqual(other: TableColOrder): boolean {
		return isShallowEqual(other, this);
	}

	private constructor(
		public readonly value: undefined | TableColOrderValue,
		private readonly priority: undefined | number,
		private readonly updatedAt: undefined | Time,
	) {}

	public next(now: Time): TableColOrder {
		const value = NEXT_SORTING[this.value ?? "none"];

		return new TableColOrder(
			value === "none" ? undefined : value,
			this.priority,
			this.value == null ? now : this.updatedAt,
		);
	}

	public prev(now: Time): TableColOrder {
		const value = PREV_SORTING[this.value ?? "none"];

		return new TableColOrder(
			value === "none" ? undefined : value,
			this.priority,
			this.value == null ? now : this.updatedAt,
		);
	}
}
